import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from "react-redux";
import typeOfContract from '../../../../constants/typeOfContract';
import { clientTotalRevenue } from "../../Services/reportingOverview.js";

// Functional component
function ReportOverviewTotalRevenue(props) {
  const [displayedRevenue, setDisplayedRevenue] = useState(0);
  const [targetRevenue, setTargetRevenue] = useState(0);
  const animationFrameRef = useRef(null);
  const reportingFinancialOverview = useSelector((state) => state.reportingFinancialOverview);
  const { forex } = useSelector((state) => state.forex);
  const settings = JSON.parse(localStorage.getItem("settings"));
  const trackReport = props.trackReport;
  const clientInvoices = props.clientInvoices;
  const projectCost = props.projectCost;
  const projects = props.projects;
  const sprints = props.sprints;
  const expenses = props.expenses;

  useEffect(() => {
    if (trackReport && projectCost && clientInvoices && projects && sprints && expenses && reportingFinancialOverview && forex && settings?.currency) {
      try {
        const totalRevenueClient = clientTotalRevenue(trackReport, projectCost, clientInvoices, projects, sprints, reportingFinancialOverview, settings.currency, forex, typeOfContract, expenses);
        const parsedRevenue = parseFloat(totalRevenueClient) || 0;
        if (!isNaN(parsedRevenue)) {
          setTargetRevenue(parsedRevenue);
        } else {
          setTargetRevenue(0);
        }
      } catch (error) {
        console.error('Error calculating total revenue:', error);
        setTargetRevenue(0);
      }
    } else {
      setTargetRevenue(0);
    }
  }, [reportingFinancialOverview, forex, settings?.currency, trackReport, clientInvoices, sprints, projectCost, projects, expenses]);

  useEffect(() => {
    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const startValue = displayedRevenue;
    const endValue = targetRevenue;
    const duration = 1000; // Animation duration in milliseconds
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      const currentValue = startValue + (endValue - startValue) * easeOut;
      setDisplayedRevenue(parseFloat(currentValue.toFixed(2)));

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayedRevenue(endValue);
        animationFrameRef.current = null;
      }
    };

    if (Math.abs(startValue - endValue) > 0.01) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [targetRevenue]);

  return (
    <div>
      <div className="overview-item">
        <div className="overview-title-outer">
          <div className="overview-title">
            Total Net Profit
          </div>
          <div className="overview-icon">
            <svg width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g filter="url(#filter0_d_2951_68)">
                <circle cx="24.5" cy="24.5" r="20.5" fill="white" />
              </g>
              <path d="M21.3298 10C20.5999 10 19.9012 10.2796 19.4019 10.7864C19.3319 10.8573 19.2864 10.9482 19.2719 11.046C19.2575 11.1439 19.2748 11.2437 19.3214 11.3313L20.8226 14.8493C20.351 15.174 20.0404 15.7103 20.0404 16.3155C20.0404 16.64 20.1293 16.9448 20.2849 17.2084C16.5326 19.5846 14 24.5995 14 28.5193C14 33.7726 18.7309 38 24.5019 38C30.2729 38 35 33.7726 35 28.5193C35 24.5986 32.4685 19.5831 28.7151 17.2074C28.8699 16.944 28.9596 16.6397 28.9596 16.3155C28.9596 15.7105 28.6505 15.1741 28.1802 14.8493L29.6776 11.3379C29.7263 11.25 29.7452 11.149 29.7314 11.0498C29.7176 10.9505 29.6719 10.8582 29.6009 10.7864C29.1017 10.2796 28.403 10 27.6731 10C26.9432 10 26.2416 10.2796 25.7424 10.7864C25.4317 11.1018 24.9805 11.2889 24.5019 11.2889C24.0313 11.2889 23.5832 11.1083 23.2729 10.8024C23.268 10.7969 23.2628 10.7916 23.2576 10.7863C22.7584 10.2796 22.0597 10 21.3298 10ZM21.3298 10.9391C21.8084 10.9391 22.2634 11.1263 22.5741 11.4417C23.0733 11.9484 23.772 12.229 24.5019 12.229C25.2318 12.229 25.9295 11.9484 26.4288 11.4417C26.434 11.4364 26.4391 11.4311 26.4441 11.4256C26.7544 11.1197 27.2025 10.9391 27.6731 10.9391C28.0427 10.9391 28.3958 11.0514 28.6806 11.2474L27.2801 14.5296C27.2319 14.5258 27.1835 14.523 27.1343 14.523H21.8685C21.8194 14.523 21.771 14.5258 21.7228 14.5296L20.3194 11.2493C20.6046 11.0521 20.959 10.9391 21.3298 10.9391ZM21.8685 15.4621H27.1343C27.6265 15.4621 28.0077 15.8335 28.0077 16.3155C28.0077 16.7974 27.6265 17.1716 27.1343 17.1716H21.8685C21.3764 17.1716 20.9952 16.7974 20.9952 16.3155C20.9952 15.8335 21.3764 15.4621 21.8685 15.4621ZM20.9856 17.8844C21.2482 18.028 21.5489 18.1107 21.8685 18.1107H27.1343C27.4542 18.1107 27.755 18.0283 28.0173 17.8844C31.4927 19.9834 34.0452 24.952 34.0452 28.5193C34.0452 33.2173 29.8013 37.0599 24.5019 37.0599C19.2025 37.0599 14.9548 33.2173 14.9548 28.5193C14.9548 24.952 17.5101 19.9834 20.9856 17.8844ZM24.5019 21.8785C24.4389 21.8783 24.3765 21.8903 24.3182 21.9139C24.2599 21.9375 24.207 21.9722 24.1624 22.016C24.1179 22.0599 24.0826 22.1119 24.0586 22.1693C24.0346 22.2266 24.0223 22.288 24.0226 22.35V23.2429C22.7248 23.4417 21.6931 24.434 21.6931 25.6812C21.6931 27.0785 22.9887 28.1553 24.5019 28.1553C25.5642 28.1553 26.355 28.8709 26.355 29.6875C26.355 30.5041 25.5642 31.2235 24.5019 31.2235C23.5746 31.2235 22.8253 30.6585 22.6757 29.9543C22.6629 29.8937 22.6381 29.8361 22.6026 29.785C22.5671 29.7338 22.5217 29.69 22.469 29.6562C22.4162 29.6224 22.3572 29.5991 22.2953 29.5878C22.2333 29.5765 22.1698 29.5774 22.1082 29.5904C21.9842 29.6158 21.8756 29.6886 21.8062 29.7927C21.7369 29.8969 21.7124 30.0238 21.7381 30.1458C21.962 31.1991 22.9008 31.9569 24.0226 32.1268V33.0225C24.0228 33.0842 24.0354 33.1452 24.0597 33.2021C24.0839 33.2589 24.1193 33.3106 24.1638 33.354C24.2083 33.3974 24.2611 33.4318 24.3191 33.4552C24.3771 33.4785 24.4392 33.4904 24.5019 33.4902C24.6279 33.4897 24.7485 33.4402 24.8376 33.3526C24.9266 33.265 24.9769 33.1464 24.9774 33.0225V32.1268C26.2766 31.929 27.3098 30.9357 27.3098 29.6875C27.3098 28.2902 26.0151 27.2162 24.5019 27.2162C23.4397 27.2162 22.6479 26.4978 22.6479 25.6812C22.6479 24.8646 23.4397 24.1452 24.5019 24.1452C25.4293 24.1452 26.1775 24.713 26.3272 25.4172C26.353 25.5389 26.4268 25.6455 26.5325 25.7138C26.6382 25.782 26.767 25.8062 26.8909 25.7811C26.9525 25.7686 27.011 25.7441 27.0631 25.7092C27.1151 25.6743 27.1596 25.6297 27.194 25.5778C27.2284 25.5259 27.252 25.4678 27.2635 25.4069C27.275 25.346 27.2741 25.2835 27.2609 25.2229C27.0369 24.1687 26.0998 23.411 24.9774 23.2419V22.35C24.9777 22.2883 24.9656 22.2272 24.9418 22.1701C24.918 22.1131 24.8831 22.0612 24.8389 22.0174C24.7948 21.9736 24.7423 21.9388 24.6845 21.915C24.6266 21.8912 24.5646 21.8788 24.5019 21.8785Z" fill="#6A7988" stroke="#6A7988" strokeWidth="0.1" />
              <defs>
                <filter id="filter0_d_2951_68" x="0" y="0" width="49" height="49" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0.468733 0 0 0 0 0.51145 0 0 0 0 0.554167 0 0 0 0.39 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2951_68" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2951_68" result="shape" />
                </filter>
              </defs>
            </svg>
          </div>
        </div>

        <div className="overview-rice">
          {displayedRevenue !== null && displayedRevenue !== undefined ? `${new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: settings?.currency || 'USD',
          }).format(displayedRevenue)}` : 'N/A'}
        </div>
      </div>
    </div>
  );
}

export default ReportOverviewTotalRevenue;
